import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { UploadIcon } from 'lucide-react'
import { FormDialog, RadioCard } from '@rentflowdev/ui'
import {
	useCreateDocumentSignatureMutation,
	useGenerateDocumentUploadUrlMutation,
	useGetMeQuery,
	useUpdateDocumentStatusMutation,
} from '@/api'

type SigningMethod = 'E_SIGNATURE' | 'MANUAL_UPLOAD'

interface SignDocumentDialogProps {
	open:          boolean
	onOpenChange:  (open: boolean) => void
	documentId:    string
	onSigned?:     () => void
}

export function SignDocumentDialog({ open, onOpenChange, documentId, onSigned }: SignDocumentDialogProps) {
	const { t } = useTranslation()

	const [method, setMethod]         = useState<SigningMethod>('E_SIGNATURE')
	const [file, setFile]             = useState<File | null>(null)
	const [isSubmitting, setIsSubmitting] = useState(false)
	const fileInputRef = useRef<HTMLInputElement>(null)

	const { data: me }           = useGetMeQuery()
	const [createSignature]      = useCreateDocumentSignatureMutation()
	const [generateUploadUrl]    = useGenerateDocumentUploadUrlMutation()
	const [updateStatus]         = useUpdateDocumentStatusMutation()

	function handleClose(open: boolean) {
		if (!open) {
			setMethod('E_SIGNATURE')
			setFile(null)
		}
		onOpenChange(open)
	}

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault()
		setIsSubmitting(true)
		try {
			let signatureProofJson: string | undefined

			if (method === 'MANUAL_UPLOAD' && file) {
				const { uploadUrl, publicUrl } = await generateUploadUrl({
					contentType: file.type,
					fileName:    file.name,
				}).unwrap()
				await fetch(uploadUrl, {
					method:  'PUT',
					body:    file,
					headers: { 'Content-Type': file.type },
				})
				signatureProofJson = JSON.stringify({ proofUrl: publicUrl })
			}

			await createSignature({
				id:   documentId,
				body: { signingMethod: method, signatureProofJson, signedByAccountId: me?.id },
			}).unwrap()

			await updateStatus({ id: documentId, body: { status: 'SIGNED_BY_ME' } }).unwrap()

			onSigned?.()
			handleClose(false)
		} catch (err) {
			console.error('signing failed:', err)
		} finally {
			setIsSubmitting(false)
		}
	}

	const isSubmitDisabled = method === 'MANUAL_UPLOAD' && !file

	return (
		<FormDialog
			open={open}
			onOpenChange={handleClose}
			title={t('documents.sign.title', { defaultValue: 'Подписать документ' })}
			submitLabel={t('documents.sign.confirm', { defaultValue: 'Подписать' })}
			cancelLabel={t('common.cancel', { defaultValue: 'Отмена' })}
			isSubmitting={isSubmitting}
			isSubmitDisabled={isSubmitDisabled}
			onSubmit={(e) => void handleSubmit(e)}
		>
			<p className="text-sm text-(--color-muted-foreground)">
				{t('documents.sign.description', { defaultValue: 'Выберите способ подписания' })}
			</p>

			<div className="flex flex-col gap-2">
				<RadioCard
					title={t('documents.sign.eSignature.title', { defaultValue: 'Электронная подпись' })}
					description={t('documents.sign.eSignature.description', { defaultValue: 'Подтвердите согласие с содержимым документа' })}
					selected={method === 'E_SIGNATURE'}
					onClick={() => setMethod('E_SIGNATURE')}
				/>
				<RadioCard
					title={t('documents.sign.manualUpload.title', { defaultValue: 'Загрузить подписанный скан' })}
					description={t('documents.sign.manualUpload.description', { defaultValue: 'Скачайте PDF, подпишите вручную и загрузите скан' })}
					selected={method === 'MANUAL_UPLOAD'}
					onClick={() => setMethod('MANUAL_UPLOAD')}
				/>
			</div>

			{method === 'MANUAL_UPLOAD' && (
				<>
					<input
						ref={fileInputRef}
						type="file"
						accept=".pdf,.jpg,.jpeg,.png"
						className="hidden"
						onChange={(e) => setFile(e.target.files?.[0] ?? null)}
					/>
					<button
						type="button"
						onClick={() => fileInputRef.current?.click()}
						className="flex flex-col items-center justify-center gap-2 rounded-(--radius) border border-dashed border-(--color-border) p-6 text-sm text-(--color-muted-foreground) transition-colors hover:border-(--color-foreground)/30 hover:text-(--color-foreground)"
					>
						<UploadIcon className="size-5" />
						{file
							? <span className="font-medium text-(--color-foreground)">{file.name}</span>
							: <span>{t('documents.sign.uploadPrompt', { defaultValue: 'Нажмите чтобы выбрать файл' })}</span>
						}
					</button>
				</>
			)}
		</FormDialog>
	)
}
